import type { Cy } from './draw'

/**
 * Exportul hartii ADM pentru prezentarea la client: PNG sau PDF, scoase
 * INTREGI IN BROWSER.
 *
 * NIMIC NU PLEACA LA SERVER. Asezarea nodurilor traieste numai in pagina, iar
 * harta unui client e exact genul de lucru care nu are ce cauta pe un drum in
 * plus. Poza o scoate cytoscape (`cy.png`), titlul si legenda se compun pe o
 * panza, iar PDF-ul il scrie `jspdf`, adus lenes - cei ~350 KB ai lui se
 * incarca abia la primul PDF cerut, nu odata cu pagina.
 *
 * LEGENDA SE DESENEAZA O SINGURA DATA, prin doua pensule. Aceeasi asezare
 * (`drawLegend`) pune semnele pe panza PNG-ului si pe foaia PDF-ului; numai
 * pensula difera (pixeli, respectiv milimetri). Doua asezari scrise separat s-ar
 * desparte la prima culoare schimbata.
 *
 * ADRESELE IP se ascund la cerere: o harta data clientului ajunge in slide-uri
 * si pe mailuri, iar adresele interne n-au ce cauta acolo. Etichetele se rescriu
 * pe instanta vie doar cat dureaza poza, apoi se pun la loc.
 */

export type ExportFormat = 'png' | 'pdf'
export type ExportArea = 'visible' | 'full'
export type ExportBackground = 'white' | 'transparent'
export type ExportScale = 1 | 2 | 3
export type Paper = 'a4' | 'a3'
export type Orientation = 'portrait' | 'landscape'

export interface ExportOptions {
  format: ExportFormat
  area: ExportArea
  /** Numai pentru PNG. PDF-ul e mereu pe alb - pe hartie transparentul nu exista. */
  background: ExportBackground
  /** Numai pentru PNG. PDF-ul isi alege singur rezolutia, dupa hartie. */
  scale: ExportScale
  paper: Paper
  title: string
  legend: boolean
  includeIps: boolean
}

export interface ExportMeta {
  organization: string
  view: string
  /** Ce s-a cerut pe ecran, in cuvinte: radacina, vederea, adancimea. */
  subtitle?: string
  /** Harta s-a oprit la bugetul serverului - se scrie pe export. */
  partial?: boolean
  date?: Date
}

/** Ce poate opri si porni pulsatia de pe harta cat se face poza. */
export interface PulseControl {
  pause(): void
  resume(): void
}

export const FOOTER_NOTE = 'Generated by OptimumDesk'

/* ------------------------------------------------------------------ nume */

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

/** Ziua LOCALA, nu cea UTC: la 01:30 in Bucuresti `toISOString` da ziua de ieri. */
export function isoDay(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export function isoMinute(date: Date): string {
  return `${isoDay(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** Fara diacritice, numai ASCII - ce se tipareste cu fonturile de baza ale PDF-ului. */
export function asciiText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[–—]/g, '-')
    .replace(/[‘’]/g, "'")
    .replace(/[“”„]/g, '"')
    .replace(/·/g, '|')
    .replace(/[^\x20-\x7e]/g, '')
}

export function slug(text: string): string {
  return asciiText(text)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/** `adm-map-<organizatie>-<view>-<YYYY-MM-DD>.<ext>`; o parte goala nu lasa doua liniute. */
export function exportFileName(meta: Pick<ExportMeta, 'organization' | 'view'>, ext: ExportFormat, date: Date): string {
  const parts = ['adm-map', slug(meta.organization), slug(meta.view), isoDay(date)].filter((p) => p !== '')
  return `${parts.join('-')}.${ext}`
}

export function viewLabel(view: string): string {
  const v = view.trim()
  return v === '' ? '' : v.charAt(0).toUpperCase() + v.slice(1)
}

/** Titlul implicit: organizatia, vederea si ziua. */
export function defaultTitle(organization: string, view: string, date: Date): string {
  const parts = [organization.trim(), 'ADM map', viewLabel(view) === '' ? '' : `${viewLabel(view)} view`, isoDay(date)]
  return parts.filter((p) => p !== '').join(' - ')
}

/* ------------------------------------------------------------------- IP */

const IPV4 = String.raw`(?<![\w.])(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(?:\/\d{1,2})?(?::\d{1,5})?(?![\w.])`
/*
 * IPv6 doar in formele care nu se confunda cu o ora: opt grupe intregi, sau
 * forma scurtata cu `::`. „08:00:00" are numai doua puncte simple si nu trece.
 */
const IPV6 = String.raw`(?<![\w:])(?:(?:[0-9a-f]{1,4}:){7}[0-9a-f]{1,4}|(?:[0-9a-f]{1,4}:){0,6}[0-9a-f]{0,4}::(?:[0-9a-f]{1,4}:){0,6}[0-9a-f]{0,4})(?:\/\d{1,3})?(?![\w:])`

const IP_TEST = new RegExp(`${IPV4}|${IPV6}`, 'i')
const IP_ALL = new RegExp(`${IPV4}|${IPV6}`, 'gi')

export function hasIp(label: string): boolean {
  return IP_TEST.test(label)
}

/**
 * Eticheta fara adrese: „SRV-DB (10.0.0.5)" -> „SRV-DB". Parantezele ramase
 * goale si separatorii de la capete pleaca odata cu adresa. O eticheta care era
 * NUMAI adresa ramane goala - mai bine un nod fara nume decat o adresa scapata.
 */
export function stripIps(label: string): string {
  if (!hasIp(label)) return label
  return label
    .replace(IP_ALL, '')
    .replace(/\(\s*[,;/|-]?\s*\)|\[\s*[,;/|-]?\s*\]/g, '')
    .replace(/\s*([,;/|])\s*(?=[,;/|]|$)/g, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\s*\n\s*/g, '\n')
    .replace(/^[\s,;/|:@-]+|[\s,;/|:@-]+$/g, '')
}

/* ------------------------------------------------------------ marimi */

/** Peisaj cand harta e mai lata decat inalta, portret altfel. */
export function orientationFor(width: number, height: number): Orientation {
  return width > height ? 'landscape' : 'portrait'
}

/** Dimensiunile foii in mm, pe orientarea data. */
export function paperSize(paper: Paper, orientation: Orientation): { width: number; height: number } {
  const [short, long] = paper === 'a3' ? [297, 420] : [210, 297]
  return orientation === 'landscape' ? { width: long, height: short } : { width: short, height: long }
}

/** Imaginea incadrata in cutie, cu raportul pastrat, pe centru. */
export function fitInBox(w: number, h: number, box: { x: number; y: number; width: number; height: number }) {
  const k = Math.min(box.width / w, box.height / h)
  const width = w * k
  const height = h * k
  return { x: box.x + (box.width - width) / 2, y: box.y + (box.height - height) / 2, width, height }
}

/*
 * Browserele refuza panzele prea mari (Chrome: 16384 px pe latura, ~268 Mpx
 * aria; Safari mai putin) si intorc o poza GOALA, fara eroare. Un parc intreg la
 * 3x trece usor de asta, deci scara se strange pana incape.
 */
const MAX_SIDE = 12000
const MAX_AREA = 80_000_000

export function safeScale(requested: number, width: number, height: number): number {
  if (width <= 0 || height <= 0) return requested
  const bySide = MAX_SIDE / Math.max(width, height)
  const byArea = Math.sqrt(MAX_AREA / (width * height))
  return Math.max(0.1, Math.min(requested, bySide, byArea))
}

/* --------------------------------------------------------------- legenda */

type Marker =
  | { type: 'shape'; shape: 'round-rectangle' | 'ellipse' | 'diamond' | 'hexagon'; colour: string }
  | { type: 'dot'; colour: string }
  | { type: 'ring'; colour: string }
  | { type: 'line'; colour: string; dashed: boolean }

export interface LegendGroup {
  title: string
  items: { label: string; marker: Marker }[]
}

/*
 * Aceleasi semne si culori ca legenda de pe ecran (`.adm-legend` din adm.css)
 * si ca desenul (`map/draw.ts`). Scrise aici fiindca draw.ts nu le exporta; o
 * culoare schimbata acolo se schimba si aici.
 */
const SHAPE_GREY = '#98a4b0'

export const LEGEND: LegendGroup[] = [
  {
    title: 'Kind',
    items: [
      { label: 'Device', marker: { type: 'shape', shape: 'round-rectangle', colour: SHAPE_GREY } },
      { label: 'Application', marker: { type: 'shape', shape: 'ellipse', colour: SHAPE_GREY } },
      { label: 'IT service', marker: { type: 'shape', shape: 'diamond', colour: SHAPE_GREY } },
      { label: 'Business service', marker: { type: 'shape', shape: 'hexagon', colour: SHAPE_GREY } },
    ],
  },
  {
    title: 'Criticality',
    items: [
      { label: 'Critical', marker: { type: 'dot', colour: '#c0392b' } },
      { label: 'Important', marker: { type: 'dot', colour: '#e67e22' } },
      { label: 'Standard', marker: { type: 'dot', colour: '#f1c40f' } },
      { label: 'Minor', marker: { type: 'dot', colour: '#95a5a6' } },
      { label: 'Not set', marker: { type: 'dot', colour: '#bdc3c7' } },
    ],
  },
  {
    title: 'State',
    items: [
      { label: 'Down', marker: { type: 'ring', colour: '#d64635' } },
      { label: 'Degraded', marker: { type: 'ring', colour: '#f0932b' } },
    ],
  },
  {
    title: 'Connections',
    items: [
      { label: 'Active (thicker = seen more often)', marker: { type: 'line', colour: '#b2bec3', dashed: false } },
      { label: 'Stopped appearing', marker: { type: 'line', colour: '#e17055', dashed: true } },
    ],
  },
]

/** O pensula: panza (pixeli) sau foaia PDF (milimetri). `y` e mijlocul randului. */
export interface Painter {
  textWidth(text: string, size: number, bold: boolean): number
  text(text: string, x: number, y: number, size: number, bold: boolean, colour: string): void
  marker(marker: Marker, x: number, y: number, size: number): void
}

export interface LegendMetrics {
  font: number
  marker: number
  gap: number
  itemGap: number
  rowHeight: number
}

/**
 * Asaza legenda pe randuri care se rup la `maxWidth`; intoarce inaltimea.
 * Cu `draw = false` doar masoara - asa se afla cat loc sa-i lase hartii.
 */
export function drawLegend(painter: Painter, x: number, y: number, maxWidth: number, m: LegendMetrics, draw: boolean): number {
  let cx = x
  let row = 0
  const centre = () => y + row * m.rowHeight + m.rowHeight / 2

  const place = (width: number): number => {
    if (cx > x && cx + width > x + maxWidth) {
      row += 1
      cx = x
    }
    const at = cx
    cx += width
    return at
  }

  for (const group of LEGEND) {
    const titleWidth = painter.textWidth(group.title + ':', m.font, true)
    /* Titlul grupului nu ramane singur la capat de rand: se muta cu primul semn. */
    const first = group.items[0]
    const firstWidth = m.marker + m.gap + painter.textWidth(first.label, m.font, false)
    if (cx > x && cx + titleWidth + m.gap + firstWidth > x + maxWidth) {
      row += 1
      cx = x
    }
    const tx = place(titleWidth + m.gap)
    if (draw) painter.text(group.title + ':', tx, centre(), m.font, true, '#4a5560')

    for (const item of group.items) {
      const labelWidth = painter.textWidth(item.label, m.font, false)
      const ix = place(m.marker + m.gap + labelWidth + m.itemGap)
      if (draw) {
        painter.marker(item.marker, ix, centre(), m.marker)
        painter.text(item.label, ix + m.marker + m.gap, centre(), m.font, false, '#4a5560')
      }
    }
    cx += m.itemGap
  }

  return (row + 1) * m.rowHeight
}

function hexagonPoints(x: number, y: number, s: number): [number, number][] {
  return [
    [x + s * 0.25, y - s / 2],
    [x + s * 0.75, y - s / 2],
    [x + s, y],
    [x + s * 0.75, y + s / 2],
    [x + s * 0.25, y + s / 2],
    [x, y],
  ]
}

function diamondPoints(x: number, y: number, s: number): [number, number][] {
  return [
    [x + s / 2, y - s / 2],
    [x + s, y],
    [x + s / 2, y + s / 2],
    [x, y],
  ]
}

/* ---------------------------------------------------------- pensula PNG */

const FONT_FAMILY = '"Segoe UI", -apple-system, "Helvetica Neue", Helvetica, Arial, sans-serif'

function canvasPainter(ctx: CanvasRenderingContext2D, unit: number): Painter {
  const font = (size: number, bold: boolean) => `${bold ? '600 ' : ''}${size}px ${FONT_FAMILY}`
  return {
    textWidth(text, size, bold) {
      ctx.font = font(size, bold)
      return ctx.measureText(text).width
    },
    text(text, x, y, size, bold, colour) {
      ctx.font = font(size, bold)
      ctx.fillStyle = colour
      ctx.textBaseline = 'middle'
      ctx.fillText(text, x, y)
    },
    marker(marker, x, y, s) {
      ctx.save()
      ctx.fillStyle = marker.colour
      ctx.strokeStyle = marker.colour
      const poly = (pts: [number, number][]) => {
        ctx.beginPath()
        pts.forEach(([px, py], i) => (i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)))
        ctx.closePath()
        ctx.fill()
      }
      if (marker.type === 'line') {
        ctx.lineWidth = 2.5 * unit
        ctx.setLineDash(marker.dashed ? [4 * unit, 3 * unit] : [])
        ctx.beginPath()
        ctx.moveTo(x, y)
        ctx.lineTo(x + s, y)
        ctx.stroke()
      } else if (marker.type === 'ring') {
        ctx.globalAlpha = 0.7
        ctx.beginPath()
        ctx.arc(x + s / 2, y, s / 2, 0, Math.PI * 2)
        ctx.fill()
        ctx.globalAlpha = 1
        ctx.fillStyle = '#b7c0c8'
        ctx.beginPath()
        ctx.arc(x + s / 2, y, s * 0.28, 0, Math.PI * 2)
        ctx.fill()
      } else if (marker.type === 'dot' || marker.shape === 'ellipse') {
        ctx.beginPath()
        ctx.arc(x + s / 2, y, s / 2, 0, Math.PI * 2)
        ctx.fill()
      } else if (marker.shape === 'diamond') {
        poly(diamondPoints(x, y, s))
      } else if (marker.shape === 'hexagon') {
        poly(hexagonPoints(x, y, s))
      } else {
        ctx.fillRect(x, y - s / 2, s, s)
      }
      ctx.restore()
    },
  }
}

/** Taie textul cu „…" pana incape. */
function fit(painter: Painter, text: string, size: number, bold: boolean, max: number): string {
  if (painter.textWidth(text, size, bold) <= max) return text
  let out = text
  while (out.length > 1 && painter.textWidth(out + '…', size, bold) > max) out = out.slice(0, -1)
  return out + '…'
}

/* --------------------------------------------------------- cytoscape */

type LiveElement = {
  data(key: string): unknown
  data(key: string, value: unknown): void
  hasClass?(c: string): boolean
}

type LiveCy = {
  png(options: Record<string, unknown>): string
  nodes(selector?: string): { length: number; forEach(fn: (el: LiveElement) => void): void; removeClass(c: string): void }
  edges(selector?: string): { length: number; forEach(fn: (el: LiveElement) => void): void }
  elements?(): { boundingBox(options?: Record<string, unknown>): { w: number; h: number } }
  width(): number
  height(): number
  batch(fn: () => void): void
}

/**
 * Ascunde adresele din etichete pe instanta vie; intoarce functia care le pune
 * la loc. Atinge numai etichetele care chiar contin o adresa.
 */
export function hideIps(cy: Cy): () => void {
  const live = cy as unknown as LiveCy
  const changed: { el: LiveElement; label: unknown }[] = []
  live.batch(() => {
    const visit = (el: LiveElement) => {
      const label = el.data('label')
      if (typeof label === 'string' && hasIp(label)) {
        changed.push({ el, label })
        el.data('label', stripIps(label))
      }
    }
    live.nodes().forEach(visit)
    live.edges().forEach(visit)
  })
  return () => {
    if (changed.length === 0) return
    live.batch(() => changed.forEach(({ el, label }) => el.data('label', label)))
  }
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

/**
 * Poza din cytoscape, cu pulsatia oprita si haloul pe valoarea lui de repaus:
 * prinsa in clipa gresita, o alarma iese aproape stearsa (`DOWN_DIM` = 0.06).
 */
async function snapshot(cy: Cy, options: { full: boolean; scale: number; bg?: string; includeIps: boolean }, pulse?: PulseControl): Promise<string> {
  const live = cy as unknown as LiveCy
  pulse?.pause()
  const pulsing = live.nodes('.pulse-on, .pulse-on-soft')
  if (pulsing.length > 0) {
    pulsing.removeClass('pulse-on pulse-on-soft')
    /* Haloul se intoarce prin tranzitie (0.55 s / 0.9 s in draw.ts). */
    await wait(950)
  }
  const restore = options.includeIps ? () => {} : hideIps(cy)
  try {
    const box = options.full && live.elements ? live.elements().boundingBox() : { w: live.width(), h: live.height() }
    const scale = safeScale(options.scale, box.w, box.h)
    const png: Record<string, unknown> = { full: options.full, scale, output: 'base64uri' }
    if (options.bg) png.bg = options.bg
    return live.png(png)
  } finally {
    restore()
    pulse?.resume()
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('The map image could not be produced.'))
    img.src = src
  })
}

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/* -------------------------------------------------------------- PNG */

/**
 * Poza cu titlu, subtitlu, legenda si nota de subsol. Fara titlu si fara
 * legenda iese poza goala a hartii, fara chenar - pentru cine o lipeste
 * intr-un slide care are deja titlul lui.
 */
export async function composePng(mapUri: string, options: ExportOptions, meta: ExportMeta, date: Date): Promise<Blob> {
  const img = await loadImage(mapUri)
  const bare = options.title.trim() === '' && !options.legend

  const u = options.scale
  const padding = bare ? 0 : 24 * u
  const width = Math.max(img.width + padding * 2, bare ? 0 : 720 * u)

  const scratch = document.createElement('canvas').getContext('2d')
  if (!scratch) throw new Error('This browser cannot draw the export.')
  const measure = canvasPainter(scratch, u)

  const metrics: LegendMetrics = { font: 12 * u, marker: 11 * u, gap: 5 * u, itemGap: 14 * u, rowHeight: 22 * u }
  const title = options.title.trim()
  const subtitle = [meta.subtitle ?? '', meta.partial ? 'Partial map: the server stopped at its item budget' : ''].filter((s) => s !== '').join(' · ')

  const titleH = bare || title === '' ? 0 : 30 * u
  const subtitleH = bare || subtitle === '' ? 0 : 20 * u
  const headH = titleH + subtitleH + (titleH + subtitleH > 0 ? 12 * u : 0)
  const legendH = options.legend ? drawLegend(measure, 0, 0, width - padding * 2, metrics, false) + 10 * u : 0
  const footH = bare ? 0 : 22 * u
  const height = padding + headH + img.height + (bare ? 0 : 12 * u) + legendH + footH + padding

  const canvas = document.createElement('canvas')
  canvas.width = Math.ceil(width)
  canvas.height = Math.ceil(height)
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('This browser cannot draw the export.')
  const p = canvasPainter(ctx, u)

  if (options.background === 'white') {
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }

  let y = padding
  const inner = width - padding * 2
  if (titleH > 0) {
    p.text(fit(p, title, 20 * u, true, inner), padding, y + titleH / 2, 20 * u, true, '#2f353b')
    y += titleH
  }
  if (subtitleH > 0) {
    p.text(fit(p, subtitle, 12 * u, false, inner), padding, y + subtitleH / 2, 12 * u, false, '#667079')
    y += subtitleH
  }
  if (headH > 0) y += 12 * u

  ctx.drawImage(img, padding + (inner - img.width) / 2, y)
  y += img.height + (bare ? 0 : 12 * u)

  if (options.legend) {
    drawLegend(p, padding, y, inner, metrics, true)
    y += legendH
  }

  if (!bare) {
    const note = `${FOOTER_NOTE} · ${isoMinute(date)}`
    p.text(note, padding, y + footH / 2, 10 * u, false, '#98a4b0')
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('The PNG could not be produced.'))), 'image/png')
  })
}

/* -------------------------------------------------------------- PDF */

type JsPdfDoc = {
  internal: { pageSize: { getWidth(): number; getHeight(): number } }
  setFont(name: string, style: string): void
  setFontSize(size: number): void
  setTextColor(colour: string): void
  setDrawColor(colour: string): void
  setFillColor(colour: string): void
  setLineWidth(w: number): void
  setLineDashPattern(pattern: number[], phase: number): void
  setGState(state: unknown): void
  GState: new (o: { opacity: number }) => unknown
  getTextWidth(text: string): number
  text(text: string, x: number, y: number, options?: Record<string, unknown>): void
  line(x1: number, y1: number, x2: number, y2: number): void
  rect(x: number, y: number, w: number, h: number, style?: string): void
  circle(x: number, y: number, r: number, style?: string): void
  lines(lines: number[][], x: number, y: number, scale?: number[], style?: string, closed?: boolean): void
  addImage(data: string, format: string, x: number, y: number, w: number, h: number, alias?: string, compression?: string): void
  setProperties(p: Record<string, string>): void
  output(type: 'blob'): Blob
}

/** 1 pt = 0.3528 mm; jsPDF da latimea textului in unitatea foii (mm). */
function pdfPainter(doc: JsPdfDoc): Painter {
  const font = (size: number, bold: boolean) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal')
    doc.setFontSize(size)
  }
  const poly = (pts: [number, number][]) => {
    const [x0, y0] = pts[0]
    const rel = pts.slice(1).map(([px, py], i) => [px - pts[i][0], py - pts[i][1]])
    doc.lines(rel, x0, y0, [1, 1], 'F', true)
  }
  return {
    textWidth(text, size, bold) {
      font(size, bold)
      return doc.getTextWidth(asciiText(text))
    },
    text(text, x, y, size, bold, colour) {
      font(size, bold)
      doc.setTextColor(colour)
      doc.text(asciiText(text), x, y, { baseline: 'middle' })
    },
    marker(marker, x, y, s) {
      doc.setFillColor(marker.colour)
      doc.setDrawColor(marker.colour)
      if (marker.type === 'line') {
        doc.setLineWidth(0.7)
        doc.setLineDashPattern(marker.dashed ? [1.2, 0.9] : [], 0)
        doc.line(x, y, x + s, y)
        doc.setLineDashPattern([], 0)
      } else if (marker.type === 'ring') {
        doc.setGState(new doc.GState({ opacity: 0.7 }))
        doc.circle(x + s / 2, y, s / 2, 'F')
        doc.setGState(new doc.GState({ opacity: 1 }))
        doc.setFillColor('#b7c0c8')
        doc.circle(x + s / 2, y, s * 0.28, 'F')
      } else if (marker.type === 'dot' || marker.shape === 'ellipse') {
        doc.circle(x + s / 2, y, s / 2, 'F')
      } else if (marker.shape === 'diamond') {
        poly(diamondPoints(x, y, s))
      } else if (marker.shape === 'hexagon') {
        poly(hexagonPoints(x, y, s))
      } else {
        doc.rect(x, y - s / 2, s, s, 'F')
      }
    },
  }
}

export async function composePdf(mapUri: string, options: ExportOptions, meta: ExportMeta, date: Date): Promise<Blob> {
  const img = await loadImage(mapUri)
  const orientation = orientationFor(img.width, img.height)
  const { jsPDF } = await import('jspdf')
  const doc = new jsPDF({ orientation, unit: 'mm', format: options.paper, compress: true }) as unknown as JsPdfDoc
  const p = pdfPainter(doc)

  const W = doc.internal.pageSize.getWidth()
  const H = doc.internal.pageSize.getHeight()
  const m = 12
  const inner = W - m * 2

  const title = options.title.trim()
  doc.setProperties({ title: asciiText(title || 'ADM map'), creator: FOOTER_NOTE })

  let y = m
  if (title !== '') {
    p.text(fit(p, title, 16, true, inner), m, y + 3.5, 16, true, '#2f353b')
    y += 9
  }
  const subtitle = [meta.subtitle ?? '', meta.partial ? 'Partial map: the server stopped at its item budget' : ''].filter((s) => s !== '').join(' | ')
  if (subtitle !== '') {
    p.text(fit(p, subtitle, 9, false, inner), m, y + 2, 9, false, '#667079')
    y += 6
  }
  y += 2

  const footY = H - 7
  const metrics: LegendMetrics = { font: 8, marker: 3, gap: 1.5, itemGap: 4, rowHeight: 5.5 }
  const legendH = options.legend ? drawLegend(p, 0, 0, inner, metrics, false) : 0
  const legendTop = footY - 5 - legendH
  const box = { x: m, y, width: inner, height: (options.legend ? legendTop - 3 : footY - 5) - y }

  /* 4 mm de aer in chenar: poza din cytoscape e taiata strans pe noduri. */
  const placed = fitInBox(img.width, img.height, { x: box.x + 4, y: box.y + 4, width: box.width - 8, height: box.height - 8 })
  doc.addImage(mapUri, 'PNG', placed.x, placed.y, placed.width, placed.height, undefined, 'FAST')
  doc.setDrawColor('#e0e5e9')
  doc.setLineWidth(0.2)
  doc.rect(box.x, box.y, box.width, box.height, 'S')

  if (options.legend) drawLegend(p, m, legendTop, inner, metrics, true)

  doc.setDrawColor('#e0e5e9')
  doc.setLineWidth(0.2)
  doc.line(m, footY - 3, W - m, footY - 3)
  p.text(FOOTER_NOTE, m, footY, 7.5, false, '#98a4b0')
  const stamp = isoMinute(date)
  p.text(stamp, W - m - p.textWidth(stamp, 7.5, false), footY, 7.5, false, '#98a4b0')

  return doc.output('blob')
}

/* ----------------------------------------------------------- intrarea */

/** Scoate fisierul si il da browserului sa-l salveze. Intoarce numele lui. */
export async function exportAdmMap(cy: Cy, options: ExportOptions, meta: ExportMeta, pulse?: PulseControl): Promise<string> {
  const date = meta.date ?? new Date()
  const pdf = options.format === 'pdf'
  /* PDF-ul: 3x ajunge la ~250 dpi pe o latura de A4, si safeScale il strange
     cand harta e mare. */
  const uri = await snapshot(
    cy,
    {
      full: options.area === 'full',
      scale: pdf ? 3 : options.scale,
      bg: pdf || options.background === 'white' ? '#ffffff' : undefined,
      includeIps: options.includeIps,
    },
    pulse,
  )
  const blob = pdf ? await composePdf(uri, options, meta, date) : await composePng(uri, options, meta, date)
  const name = exportFileName(meta, options.format, date)
  download(blob, name)
  return name
}
